export const DEFAULT_POST_LOGIN_PATH = '/platforms';

function pathOnly(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const withoutQuery = raw.split('?')[0].split('#')[0];
  return withoutQuery.toLowerCase();
}

/**
 * Absolute external URLs are never followed from the SPA (open-redirect guard).
 * Same-origin absolute URLs are reduced to path+query+hash.
 */
function sanitizeAbsoluteReturnUrl(next) {
  try {
    const parsed = new URL(next, typeof window !== 'undefined' ? window.location.origin : 'https://localhost');
    if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || DEFAULT_POST_LOGIN_PATH;
    }
  } catch {
    // fall through
  }

  return DEFAULT_POST_LOGIN_PATH;
}

export function readReturnUrlFromSearchParams(searchParams) {
  if (typeof window !== 'undefined' && window.location?.search) {
    const raw = window.location.search;
    const marker = 'returnUrl=';
    const markerIndex = raw.indexOf(marker);
    if (markerIndex >= 0) {
      const nested = decodeURIComponent(raw.slice(markerIndex + marker.length));
      if (nested.startsWith('/api/sso/')) {
        return nested;
      }
    }
  }

  return String(searchParams?.get('returnUrl') || '').trim();
}

/**
 * Resolves where the portal should land immediately after login.
 * Default is Platforms unless we are continuing an external/platform SSO flow or Profile.
 */
export function resolvePostLoginPath(returnUrl) {
  const next = String(returnUrl || '').trim();
  if (!next) {
    return DEFAULT_POST_LOGIN_PATH;
  }

  if (next.startsWith('http://') || next.startsWith('https://')) {
    return sanitizeAbsoluteReturnUrl(next);
  }

  // Protocol-relative and other schemes must not be followed.
  if (next.startsWith('//') || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(next)) {
    return DEFAULT_POST_LOGIN_PATH;
  }

  if (
    next.startsWith('/api/')
    || next.startsWith('/signin-')
    || next.startsWith('/signout-')
    || next.startsWith('/sso/')
  ) {
    return next;
  }

  const route = pathOnly(next);
  if (route === '/profile' || route.startsWith('/profile/')) {
    return next.startsWith('/') ? next : `/${next}`;
  }

  if (route === '/platforms' || route === '/tools') {
    return DEFAULT_POST_LOGIN_PATH;
  }

  return DEFAULT_POST_LOGIN_PATH;
}

export function redirectToPostLoginTarget(returnUrl, navigate) {
  const target = resolvePostLoginPath(returnUrl);

  if (target.startsWith('http://') || target.startsWith('https://')) {
    // resolvePostLoginPath already blocks external hosts; keep a hard stop here.
    window.location.assign(sanitizeAbsoluteReturnUrl(target));
    return;
  }

  if (target.startsWith('/api/') || target.startsWith('/signin-') || target.startsWith('/signout-') || target.startsWith('/sso/')) {
    window.location.assign(target);
    return;
  }

  navigate(target || DEFAULT_POST_LOGIN_PATH, { replace: true });
}
